// The aim ray (AIM-SKY, 2026-10-11): where the centre ray meets the ground,
// for the inspect target, the view's centre range and the VR optic's range.
// Pure (no three, no DOM): scripts/check-aim.mjs drives it in Node on the
// live bake's GROUND-M nodes.
//
// The ground a ray can see, near → far (rearch-r8k, ONE GROUND: the client's
// stand / aim / label reads are the game server's skin, client/src/ground.js):
//   exact  THE skin where a GROUND-M L0 node (groundm/L0, GM03, 128 m) is in
//          the client's one wasm Ground (ClientGround.standHeight: the node's
//          triangle, under a GM03 hole the game/holefall.js terrain/L0
//          fallback): the player's 3×3 block plus the nodes AimGround owns
//          there (tag 'aim').
//   env    in the frame, where no node is in: the node's BOX from
//          groundm/index.json — ClientGround.lower / upper, the node's lowest
//          / highest vertex (every triangle of the node lies between). Below
//          `lower` the ray is under the ground somewhere at or before (an
//          envelope hit, never final); above `upper` over the whole node (a
//          line's lowest point on a box is an end) it is decided clear
//          without the node; between, undecided: that node is needed.
//   far    outside the frame: farHf (the ring sampler's 64 m exact DEM posts,
//          then the 256 m horizon), the only data there is.
//
// So the march (aimMarch) answers three ways:
//   hit, final     the first exact hit (or a far hit), and no node before it
//                  was undecided;
//   hit, !final    an envelope hit (the true hit lies at or before it: the
//                  box's lower is never above the ground), or an exact hit
//                  behind an undecided node;
//   null, !final   no hit, but the ray passed through a node's box (under its
//                  upper) where the node is not in: undecided, NOT sky.
// `need` lists the L0 nodes of the undecided stretches, nearest first;
// AimGround fetches them and the caller marches again. "Open sky" is only
// ever a FINAL null.
//
// The old main.js `_rayMarch` stepped d ×1.3 from 5 m (strides of 230 m at
// 1 km, 1 km at 4.5 km) over the L5 envelope and called every miss sky: from
// Samedan 119 of 200 rays at the slope below the Il Gnif mast read open sky,
// none hit within 20 m (check-aim.mjs, the red twin).

// a `g` without envTop (the gates' synthetic envelopes: a lower envelope with
// no upper bound): its worst deficit under the true ground must stay below
// this, and a stretch of the ray this close to the envelope is undecided (the
// pre-r8k L5 rule: 192.6 m at a cliff on the reference world)
export const ENV_SLACK_M = 220;
export const EXACT_STEP_M = 0.5;      // 1 m let a grazing sliver at 588 m through
export const RANGE_MAX_M = 130000;    // the camera far plane
const NEAR_M = 0.5;
// the L0 node lattice (client/src/ground.js nodeKey, game/ground.js _need): a
// node covers x in [NODE_O + NODE_M tx, NODE_O + NODE_M (tx + 1))
export const NODE_M = 128;
const NODE_O = 0.25;
const GM_MAGIC = 0x33304d47;          // 'GM03' (groundm/codec.js MAGIC)

// L0 node key (ty * 65536 + tx; the old terrain/L0 tile grid is the same
// lattice: 256 cells of 0.5 m from 0.25). x / z under 0.25 (the frame's first
// quarter metre) read node 0 — makeAimGround clamps its reads the same way
export function l0Key(x, z, cell = 0.5) {
  const gx = Math.max(0, x / cell - 0.5), gz = Math.max(0, z / cell - 0.5);
  return Math.floor(gz / 256) * 65536 + Math.floor(gx / 256);
}

// g: {
//   exact(x, z) → y | null   the true ground where its node is in
//   env(x, z)   → y | null   the in-frame lower envelope (≤ the ground)
//   envTop(x, z) → y | null  optional: the in-frame UPPER bound over the
//                            L0 node at (x, z) (≥ the ground everywhere in
//                            it: ClientGround.upper). With it the march walks
//                            node boxes (NODE_M): a node the ray crosses above
//                            envTop is decided clear, one it crosses under
//                            env is an envelope hit, else undecided. Without
//                            it (or where it is null) the ENV_SLACK_M rule
//                            on env at envStep strides
//   envStep                 env's post spacing (m)
//   far(x, z)   → y | null   out-of-frame ground; far.step(x, z) → spacing | null
//   inFrame(x, z) → bool
//   top                     highest ground in the frame (m), optional
//   key(x, z)               the L0 node to fetch for (x, z) (default l0Key)
//   owns(k) → bool           k is one of AimGround's own nodes (its reads are
//                            reported in `reads`: the held working set)
// }
// opts: { maxNeed } — stop collecting needed nodes after this many;
//       { maxDist } — march no further than this (m): the label line of sight
//       (labelsight.js) asks only whether the ground rises into the ray BEFORE
//       its target, so the march ends there with one last sample exactly at
//       maxDist (a stride never steps over the end). Default RANGE_MAX_M;
//       { skip } — over an exact node, jump to the node's exit when the ray
//       stays above g.envTop there on the way through it (boxSkip). Exact:
//       no triangle of the node stands above its highest vertex, and the
//       ray's lowest point on a box is one of its two ends — so no crossing
//       is stepped over. The labels use it (labelsight.js: a 9 km line at
//       0.5 m strides is 19 k samples, ~10 ms); the aim keeps its plain
//       0.5 m march over exact nodes;
//       { from } — start the march at this distance (m) instead of the eye:
//       the stretch before it was proven clear by an earlier march of the SAME
//       line (labelsight.js resumes a label's line there, so its nodes need
//       not all be resident at once — LABELSFIX 2026-10-12).
// Returns { dist, x, y, z, src: 'exact'|'env'|'far'|null, final, need: [keys],
// undecided: the distance of the first undecided sample | null,
// clear: the last sample before the first undecided one (from `from` on, every
// sample up to it was decided above the ground: the proven-clear prefix),
// reads: [k, d, …] every g.owns node the march read exactly, with the
// distance of its last sample there (a line enters a node once) }.
// wantOf(r, from) → the nodes a re-march from `from` still reads or needs.
export function aimMarch(O, D, g, opts = {}) {
  const maxNeed = opts.maxNeed ?? 64;
  const clip = opts.maxDist != null;
  const skip = !!opts.skip && !!g.envTop;
  const end = clip ? Math.min(RANGE_MAX_M, opts.maxDist) : RANGE_MAX_M;
  const key = g.key || l0Key;
  const need = [];
  const seen = new Set();
  const owns = g.owns || null;
  const reads = [];
  let rk = -1, ri = -1;                // the current own node, its d slot
  // the envelope's own post spacing (no envTop): a bump between two posts is
  // within ENV_SLACK_M of the ray there, so its node is needed anyway
  const envStride = Math.max(1, g.envStep || 16);
  const ask = (x, z) => {
    const k = key(x, z);
    if (need.length < maxNeed && !seen.has(k)) { seen.add(k); need.push(k); }
  };
  let first = null;                    // distance of the first undecided sample
  const top = g.top ?? Infinity;
  const at = (d) => ({ x: O.x + D.x * d, y: O.y + D.y * d, z: O.z + D.z * d });
  // one ground at (x, z) for the bisection: whichever source answered
  const ground = (src, x, z) => {
    if (src === 'exact') return g.exact(x, z) ?? (g.env ? g.env(x, z) : null);
    if (src === 'env') return g.env(x, z);
    return g.far ? g.far(x, z) : null;
  };
  const done = (src, lo, hi) => {
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2, p = at(mid), y = ground(src, p.x, p.z);
      if (y !== null && p.y <= y) hi = mid; else lo = mid;
    }
    const p = at(hi);
    // an envelope hit is never final; a later hit is final only when every
    // stretch before it was decided (no node needed)
    return { dist: hi, x: p.x, y: p.y, z: p.z, src, final: src !== 'env' && need.length === 0, need, undecided: first, clear, reads };
  };
  const from = opts.from > 0 ? Math.min(opts.from, end) : 0;
  let prev = from, d = from > 0 ? from : Math.min(NEAR_M, end);
  let clear = from;                    // the proven-clear prefix (see Returns)
  while (d <= end) {
    const x = O.x + D.x * d, y = O.y + D.y * d, z = O.z + D.z * d;
    let step;
    if (g.inFrame(x, z)) {
      if (y > top && D.y >= 0) {
        // above every summit of the frame and climbing: nothing in it can
        // stop the ray — jump to where it leaves the frame
        step = Math.max(envStride, frameExit(O, D, d, g) - d);
      } else {
        const ge = g.exact(x, z);
        if (ge !== null) {
          if (owns) {
            const k = key(x, z);
            if (k !== rk) { rk = k; ri = owns(k) ? reads.push(k, d) - 1 : -1; } else if (ri >= 0) reads[ri] = d;
          }
          if (y <= ge) return done('exact', prev, d);
          step = EXACT_STEP_M;
          if (skip) { const s = boxSkip(O, D, d, x, z, y, g); if (s > step) step = s; }
        } else {
          const up = g.envTop ? g.envTop(x, z) : null;
          if (up !== null) {
            // the node's box (see g.envTop): the ray's lowest point on it is
            // an end (the node's exit, or where it leaves the frame)
            const t1 = Math.min(nodeExit(O, D, x, z), frameExit(O, D, d, g));
            const y1 = Number.isFinite(t1) ? O.y + D.y * t1 : (D.y >= 0 ? y : -Infinity);
            if (Math.min(y, y1) <= up) {
              // under the node's highest vertex: undecided, the node is needed
              if (first === null) first = d;
              ask(x, z);
              const lo = g.env ? g.env(x, z) : null;
              if (lo !== null && y <= lo) return done('env', prev, d);
              // it sinks under the node's lowest vertex before the exit (and
              // before the march's end: a label line past its target is not
              // the label's business)
              if (lo !== null && y1 <= lo) {
                const tc = d + (y - lo) / -D.y;
                if (tc <= end) return done('env', d, tc);
              }
            }
            step = Number.isFinite(t1) && t1 > d ? t1 - d + 1e-3 : envStride;
          } else {
            const gv = g.env ? g.env(x, z) : null;
            if (gv !== null) {
              if (y - gv <= ENV_SLACK_M) {
                if (first === null) first = d;
                ask(x, z);
              }
              if (y <= gv) return done('env', prev, d);
            } else {
              // no envelope yet (the GM index still in flight): undecided too
              if (first === null) first = d;
              ask(x, z);
            }
            step = envStride;
          }
        }
      }
    } else {
      const gf = g.far ? g.far(x, z) : null;
      if (gf !== null && y <= gf) return done('far', prev, d);
      const s = g.far && g.far.step ? g.far.step(x, z) : null;
      // no grid answers out here: nothing further can stop the ray
      if (s === null || s === undefined) {
        if (gf === null) break;
        step = 64;
      } else step = s;
    }
    // no hit here: decided clear unless this (or an earlier) sample was not
    if (first === null) clear = d;
    prev = d;
    // the last stride lands on `end` itself, then the march stops
    d = clip && d < end && d + step > end ? end : d + step;
  }
  return { dist: null, x: null, y: null, z: null, src: null, final: need.length === 0, need, undecided: first, clear, reads };
}

// the L0 nodes a march of the same line from `from` (m) on still reads or
// needs: r.need plus the own nodes r read at or past `from` (AimGround.hold)
export function wantOf(r, from = 0) {
  const w = r && r.need ? r.need.slice() : [];
  const rd = r && r.reads;
  if (rd) for (let i = 0; i < rd.length; i += 2) if (rd[i + 1] >= from) w.push(rd[i]);
  return w;
}

// the ray parameter where it leaves the xz box [x0, x1] × [z0, z1]
function boxExit(O, D, x0, x1, z0, z1) {
  let t = Infinity;
  if (D.x > 0) t = (x1 - O.x) / D.x; else if (D.x < 0) t = (x0 - O.x) / D.x;
  if (D.z > 0) t = Math.min(t, (z1 - O.z) / D.z); else if (D.z < 0) t = Math.min(t, (z0 - O.z) / D.z);
  return t;
}

// the ray parameter where it leaves the L0 node (x, z) lies in (the NODE_M
// lattice from NODE_O; Infinity for a vertical ray)
function nodeExit(O, D, x, z) {
  const tx = Math.floor((x - NODE_O) / NODE_M), ty = Math.floor((z - NODE_O) / NODE_M);
  const x0 = NODE_O + NODE_M * tx, z0 = NODE_O + NODE_M * ty;
  return boxExit(O, D, x0, x0 + NODE_M, z0, z0 + NODE_M);
}

// opts.skip: how far the ray (at d, point x/y/z over an exact node) may jump
// — to just past the node, when its lowest point there (an end: it is a
// line) stays above the node's highest vertex (g.envTop); else 0. No closure
// per sample (the march's hot path)
function boxSkip(O, D, d, x, z, y, g) {
  const up = g.envTop(x, z);
  if (up === null) return 0;
  const t1 = nodeExit(O, D, x, z);
  if (!(t1 > d) || !Number.isFinite(t1)) return 0;
  return Math.min(y, O.y + D.y * t1) > up ? t1 - d + 1e-3 : 0;
}

// distance at which the ray (now at d, inside the frame) leaves it
function frameExit(O, D, d, g) {
  const X = g.extentX, Z = g.extentZ;
  if (!(X > 0 && Z > 0)) return d + 64;
  let t = Infinity;
  if (D.x > 0) t = Math.min(t, (X - O.x) / D.x); else if (D.x < 0) t = Math.min(t, -O.x / D.x);
  if (D.z > 0) t = Math.min(t, (Z - O.z) / D.z); else if (D.z < 0) t = Math.min(t, -O.z / D.z);
  // a vertical ray never leaves the frame: nothing above `top` stops it
  return Number.isFinite(t) ? Math.max(d, t + 0.01) : Infinity;
}

// The aim's own L0 nodes: the ones aimMarch asked for that the ClientGround
// does not already hold (the player's 3×3 block needs no fetch: it is
// resident for the march). `fetchNode(tx, ty, lane)` → Promise<ArrayBuffer |
// null> of the node's GM03 bytes (groundm/L0/<tx>_<ty>.bin) is injected
// (main.js: fetchAsset 'near' for the aim, 'bulk' for the labels;
// check-aim.mjs: the bake on disk). A landed node goes into the client's ONE
// wasm Ground (ground.own(k, 'aim', bytes)); an evicted one leaves it
// (ground.disown(k, 'aim'): a block node stays). The nodes are the bake's
// GM03 bytes — nothing is invented, a hit on them is a hit on THE ground
// (the skin the server stands on).
//
// A HELD working set (LABELS-CHURN 2026-10-20), not an LRU by landing: each
// lane publishes the nodes its live lines still read or need (hold(lane,
// keys): the aim's last march; the labels' undecided lines past their
// proven-clear prefix — wantOf). A node is evicted only when no lane holds
// it (the oldest such first); the aim may take a node only the labels hold;
// a fetch that would have to evict a held node is not started (it waits
// queued, its line stays undecided and keeps its verdict). Before, the
// shared 64-tile LRU evicted by landing order (a march's reads never
// refreshed it), so the 24 label lines, which all leave the eye on one stem,
// evicted the tiles the next line and the aim needed: the same tile fetched
// again and again, ~1.5 k L0 requests in a short walk (check-labels churn).
// cap AIM_CAP nodes (owned + in flight). A decoded GM L0 node costs ~1.7–2.4
// MB of wasm heap (the old terrain/L0 tile 132 KB): 48 nodes ≈ 80–115 MB
// (192 old tiles were 25 MB); a 16 km frame diagonal crosses ≤ ~256 nodes,
// so a long label line resumes from its proven-clear prefix (labelsight.js)
// rather than holding every node at once.
export const AIM_CAP = 48;
export class AimGround {
  // ground: the ClientGround (own / disown / holds) | null (the gates' bare
  // queue rows: the keys alone). held false: the gates' red twin — the
  // pre-LABELS-CHURN LRU (evict the oldest-landed node on landing, holds
  // ignored)
  constructor({ meta, ground = null, fetchNode, cap = AIM_CAP, inflight = 4, labelSlots = Math.max(1, inflight - 2), held = true }) {
    const L = meta.levels.L0;
    this.nx = L.nx; this.ny = L.ny;
    this.ground = ground;
    this.fetchNode = fetchNode;
    this.cap = cap; this.maxInflight = inflight;
    // the labels' lane holds at most this many of the in-flight slots: its
    // 'bulk' fetches may be held (assetq pause) and must not starve the aim
    this.labelSlots = labelSlots; this._pendLabels = 0;
    this.held = held;
    this.holds = { aim: new Set(), labels: new Set() };   // hold(): what each lane still needs
    this.tiles = new Map();     // key → true: the nodes owned ('aim') in the ground, insertion = last landed / held
    this.pending = new Set();   // keys in flight
    this.queue = [];            // the aim's keys waiting for a slot, nearest first
    this.queueLabels = [];      // the labels' (labelsight.js): served after the aim's
    this.failed = new Map();    // key → retry-after ms
    this._waiters = [];         // settle(): resolved when the queue drains
    this.gen = 0;               // bumps when a node lands
    this._now = 0;              // the last request's clock (a hold's pump)
  }

  // k is in the ground: owned here, or held by the ground itself (the block)
  has(k) { return this.tiles.has(k) || (!!this.ground && this.ground.holds(k)); }

  // where key k stands: 'tile' (in the ground), 'pending' (in flight),
  // 'queued' (waiting on a lane), 'failed' (retry not due), null (none of
  // these — never asked, evicted, or dropped from a replaced waiting list)
  status(k, now = 0) {
    if (this.has(k)) return 'tile';
    if (this.pending.has(k)) return 'pending';
    if (this.queue.includes(k) || this.queueLabels.includes(k)) return 'queued';
    const f = this.failed.get(k);
    return f !== undefined && now < f ? 'failed' : null;
  }

  // lane's live lines still read or need `keys` (replaces the lane's last
  // set): those nodes are not evicted; the owned ones move to the young
  // end, so the oldest unheld node goes first
  hold(lane, keys, now = this._now) {
    const s = new Set(keys);
    this.holds[lane === 'labels' ? 'labels' : 'aim'] = s;
    for (const k of s) if (this.tiles.has(k)) this._touch(k);
    this._pump(now);
  }

  // a node leaves the working set and the ground (unless the block holds it)
  _evict(k) {
    this.tiles.delete(k);
    if (this.ground) this.ground.disown(k, 'aim');
  }

  // room for one more fetch on `lane`: under the cap, else evict the oldest
  // node no lane holds (the aim: else the oldest only the labels hold);
  // false = every owned node is still needed, the fetch waits
  _room(lane) {
    if (this.tiles.size + this.pending.size < this.cap) return true;
    const A = this.holds.aim, L = this.holds.labels;
    let alt;
    for (const k of this.tiles.keys()) {
      if (!A.has(k) && !L.has(k)) { this._evict(k); return true; }
      if (alt === undefined && lane === 'aim' && !A.has(k)) alt = k;
    }
    if (alt === undefined) return false;
    this._evict(alt);
    return true;
  }

  // ask for `keys` (nearest first): queued behind nothing older — a new aim
  // replaces the waiting list (in-flight loads finish and are kept). Two
  // lanes share the one working set and the in-flight slots: 'aim' (inspect,
  // the centre range, the VR rangefinder) and 'labels' (the label line of
  // sight, labelsight.js); each request replaces only its own lane's waiting
  // list, a free slot always serves the aim's lane first, and the labels
  // hold at most labelSlots of the slots. A node the ground holds is not
  // fetched.
  request(keys, now = 0, lane = 'aim') {
    this._now = now;
    const q = [];
    for (const k of keys) {
      if (this.tiles.has(k)) { this._touch(k); continue; }
      if (this.has(k) || this.pending.has(k)) continue;
      const tx = k % 65536, ty = (k - tx) / 65536;
      if (tx >= this.nx || ty >= this.ny) continue;
      const f = this.failed.get(k);
      if (f !== undefined && now < f) continue;
      q.push(k);
    }
    if (lane === 'labels') this.queueLabels = q; else this.queue = q;
    this._pump(now);
    return q.length + this.pending.size;
  }

  // resolves (true) once nothing is queued or in flight
  settle() {
    if (!this._waiting() && !this.pending.size) return Promise.resolve(true);
    return new Promise((res) => this._waiters.push(res));
  }

  _waiting() { return this.queue.length + this.queueLabels.length; }

  _touch(k) { const t = this.tiles.get(k); this.tiles.delete(k); this.tiles.set(k, t); }

  _pump(now) {
    while (this.pending.size < this.maxInflight) {
      let q, lane;
      if (this.queue.length) { q = this.queue; lane = 'aim'; }
      else if (this.queueLabels.length && this._pendLabels < this.labelSlots) { q = this.queueLabels; lane = 'labels'; }
      else break;
      const k = q[0];
      if (this.pending.has(k) || this.has(k)) { q.shift(); continue; }   // both lanes asked / the block landed
      if (this.held && !this._room(lane)) break;
      q.shift();
      this.pending.add(k);
      if (lane === 'labels') this._pendLabels++;
      const tx = k % 65536, ty = (k - tx) / 65536;
      Promise.resolve().then(() => this.fetchNode(tx, ty, lane)).then((ab) => {
        // the GM03 L0 node (tx, ty), checked before the wasm sees it (a
        // refused node must not stay half-added in the ground)
        if (!isNode(ab, tx, ty)) throw new Error('aim: node');
        // in the ground once adopted (ground.js own: the worker's index, quest-move)
        return this.ground ? this.ground.own(k, 'aim', ab) : null;
      }).then(() => {
        this.tiles.set(k, true);
        if (!this.held) while (this.tiles.size > this.cap) this._evict(this.tiles.keys().next().value);
        this.gen++;
      }).catch(() => { this.failed.set(k, now + 5000); }).finally(() => {
        this.pending.delete(k);
        if (lane === 'labels') this._pendLabels--;
        this._pump(now);
        if (!this._waiting() && !this.pending.size) {
          const w = this._waiters; this._waiters = [];
          for (const r of w) r(true);
        }
      });
    }
  }
}

// a GM03 node's bytes at L0 (tx, ty) (groundm/codec.js header: magic u32,
// level u8 @4, tx i32 @8, ty i32 @12)
function isNode(ab, tx, ty) {
  if (!ab || !(ab.byteLength >= 40)) return false;
  const dv = ArrayBuffer.isView(ab) ? new DataView(ab.buffer, ab.byteOffset, ab.byteLength) : new DataView(ab);
  return dv.getUint32(0, true) === GM_MAGIC && dv.getUint8(4) === 0 && dv.getInt32(8, true) === tx && dv.getInt32(12, true) === ty;
}

// aimMarch over the client's chain: get.ground() → the ClientGround
// (client/src/ground.js: standHeight = THE skin where its node is in, the
// block's or AimGround's; lower / upper = the node's box from
// groundm/index.json) | null, get.farHf() → the main.js far chain (with
// .step) | null. One object per world; it reads the current ground / farHf
// through the getters each call. The lattice starts at 0.25 (NODE_O): a read
// in the frame's first quarter metre is clamped onto node 0 (l0Key's rule —
// else a line out over the W / N edge would ask for a node it never reads).
export function makeAimGround(meta, aim, get) {
  const ex = meta.bbox.e1 - meta.bbox.e0, ez = meta.bbox.n1 - meta.bbox.n0;
  const top = meta.elevation && Number.isFinite(meta.elevation.elev_max_m) ? meta.elevation.elev_max_m : Infinity;
  const lat = (v) => (v < NODE_O ? NODE_O : v);
  const g = {
    extentX: ex, extentZ: ez, top, envStep: NODE_M,
    // the reads of AimGround's own nodes (aimMarch `reads` → wantOf → hold)
    owns: aim ? (k) => aim.tiles.has(k) : null,
    inFrame: (x, z) => x >= 0 && z >= 0 && x <= ex && z <= ez,
    exact: (x, z) => {
      const G = get.ground();
      return G ? G.standHeight(lat(x), lat(z)) : null;
    },
    // the node's lowest vertex (≤ the skin everywhere in it); no index yet:
    // the far chain, as before the envelope landed
    env: (x, z) => {
      const G = get.ground();
      const y = G ? G.lower(lat(x), lat(z)) : null;
      if (y !== null) return y;
      const f = get.farHf();
      return f ? f(x, z) : null;
    },
    // the node's highest vertex (≥ the skin everywhere in it)
    envTop: (x, z) => {
      const G = get.ground();
      return G ? G.upper(lat(x), lat(z)) : null;
    },
    far: null,
  };
  const march = (O, D, opts) => {
    const f = get.farHf();
    g.far = f || null;
    return aimMarch(O, D, g, opts);
  };
  // the exact ground only (the skin, in the frame) | null: labelsight.js
  // keeps it as the label's target ground once seen (a later eviction must
  // not drop the target onto the envelope)
  march.exact = (x, z) => (g.inFrame(x, z) ? g.exact(x, z) : null);
  // the ground at (x, z) on the same chain: exact, else the node's lower in
  // the frame (never above the ground), else the far chain (labelsight.js
  // lifts a label's target over it)
  march.ground = (x, z) => {
    if (g.inFrame(x, z)) {
      const y = g.exact(x, z);
      return y !== null ? y : g.env(x, z);
    }
    const f = get.farHf();
    return f ? f(x, z) : null;
  };
  return march;
}
